import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { format } from 'date-fns';
import ArrayHelper from 'foundation/array-helper';
import gtm from 'general/js/gtm';
import { getPlayerSitecoreData } from 'general/js/helpers';
import { LazyImage } from 'project/lazy-image';
import { playerSitecoreData } from 'components/live-player';
import LiveStatsRow from './live-stats-row';
import { statsNames } from './enums';

class LiveStatsCard extends React.Component {
    constructor(props) {
        super(props);

        this.observer = null;
        this.rowInstances = [];
        this.rootRef = React.createRef();
    }

    componentDidMount() {
        this.checkNodes();
        this.createAnimation();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.filters !== this.props.filters) {
            this.createAnimation();
        }

        this.checkNodes();
    }

    componentWillUnmount() {
        this.deleteObserver();
    }

    checkNodes() {
        this.rowInstances = this.rowInstances
            .filter((instance) => instance != null);
    }

    createAnimation() {
        if (this.props.shouldAnimated && this.rootRef.current) {
            this.createObserver(this.rootRef.current);
        }
    }

    createObserver(refNode) {
        if (this.observer !== null) return;

        this.observer = new IntersectionObserver((observables) => {
            const observable = observables[0];

            if (observable.intersectionRatio > 0) {
                this.rowInstances.forEach((rowRef) => rowRef.animate());
                this.deleteObserver();
            }
        });

        this.observer.observe(refNode);
    }

    deleteObserver() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    onClickViewAll = () => {
        const { model, showAll } = this.props;

        this.sendTracking();
        showAll(model.name);
    }

    sendTracking() {
        gtm.push('View all stats', 'Click', `View All Stats > ${window.location.href}`);
    }

    getOptions(name) {
        switch (name) {
            case statsNames.FAIRWAY_HITS:
            case statsNames.GREENS_IN_REGULATION:
            case statsNames.HARDEST_GREENS_TO_REACH:
                return {
                    isProgress: true,
                    showPercentage: true,
                    min: 0,
                    max: 100
                };
            case statsNames.AVERAGE_NUMBER_OF_PUTTS:
                return {
                    isProgress: true,
                    min: 1,
                    max: 2,
                    toFixed: 2
                };
            case statsNames.HARDEST_GREENS_TO_PLAY:
                return {
                    isProgress: true,
                    min: 1,
                    max: 2,
                    toFixed: 2
                };
            case statsNames.BIGGEST_HITTERS:
                return {
                    isProgress: true,
                    min: 200,
                    max: 500
                };
            case statsNames.HARDEST_HOLES:
            case statsNames.EASIEST_HOLES:
                return {
                    isCentered: true,
                    min: -0.6,
                    max: 0.6,
                    toFixed: 2,
                    easiestOrHardest:true
                };
            default:
                return {};
        }
    }

    renderRows(options) {
        const { sitecorePlayers, model, isFull } = this.props;

        const slicedStats = isFull ? model.stats : model.stats.slice(0, 3);

        return slicedStats.map((row, i) => {
            let rowModel = row;

            if (row.playerId && !row.sitecoreData) {
                const playerSD = getPlayerSitecoreData(sitecorePlayers, row.playerId) || playerSitecoreData;

                rowModel = Object.assign(
                    {},
                    row,
                    { sitecoreData: playerSD }
                );
            }

            return (
                <LiveStatsRow
                    {...this.props}
                    ref={(instance) => {
                        this.rowInstances[i] = instance;
                    }}
                    key={row.playerId || row.holeId || row.statsId}
                    model={rowModel}
                    options={options}
                />);
        });
    }

    renderViewAll() {
        const { model, targetsConfig, hasViewAll } = this.props;

        if (targetsConfig) {
            return <a
                className="live-stats-card__view-all"
                onClick={() => this.sendTracking()}
                href={targetsConfig.allResultsUrl}
                target="_blank"
                rel="noopener noreferrer"
            >View all</a>;
        }

        if (hasViewAll) {
            return <button
                className="live-stats-card__view-all"
                type="button"
                onClick={this.onClickViewAll}>View all
            </button>;
        }

        if (model.targetUrl) {
            return <a
                className="live-stats-card__view-all"
                onClick={() => this.sendTracking()}
                href={model.targetUrl}>View more statistics</a>;
        }

        return null;
    }

    render() {
        const {
            model,
            datePublished,
            isFull,
        } = this.props;

        if (!model ||
            !model.sitecoreData ||
            ArrayHelper.isEmpty(model.stats)
        ) {
            return null;
        }

        const { sitecoreData } = model;
        const options = this.getOptions(model.name);
        const stateCss = classNames({
            'has-details': isFull,
            'has-pubdate': datePublished
        });

        return (
            <div
                className={`live-stats-card ${stateCss}`}
                ref={this.rootRef}
            >
                <div className="live-stats-card__patron of-contain">
                    <LazyImage image={'/-/media/Patrons/nttdata-logo-white.png'} />
                </div>
                <div className="live-stats-card__bg of-contain">
                    <LazyImage image={sitecoreData.statsSrc} />
                </div>
                {
                    datePublished &&
                    <time className="live-stats-card__pubdate">
                        {format(datePublished, 'h:mm a')}
                    </time>
                }
                <h3 className="live-stats-card__title">{sitecoreData.title}</h3>
                <div className="live-stats-card__subtitle copy">{sitecoreData.subtitle}</div>
                <div className="live-stats-card__list">
                    {this.renderRows(options)}
                </div>
                {
                    model.body &&
                    <div
                        className="live-stats-card__body copy"
                        dangerouslySetInnerHTML={{ __html: model.body }} />
                }
                {this.renderViewAll()}
            </div>
        );
    }
}

LiveStatsCard.defaultProps = {
    isInteractive: true
};

LiveStatsCard.propTypes = {
    model: PropTypes.shape({
        sitecoreData: PropTypes.shape({
            title: PropTypes.string,
            subtitle: PropTypes.string,
            statsSrc: PropTypes.string,
        }),
        stats: PropTypes.array,
        targetUrl: PropTypes.string,
    }),
    datePublished: PropTypes.string,
    widgetUrl: PropTypes.string,
    isFull: PropTypes.bool,
    isInteractive: PropTypes.bool,
    hasViewAll: PropTypes.bool,
    shouldAnimated: PropTypes.bool,
    showAll: PropTypes.func,
};

export default LiveStatsCard;
